import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatBRL, formatDate, formatDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Textarea } from "@/components/ui/textarea";
import {
  Check, ChevronsUpDown, Download, Eraser, Mail, Plus, MoreHorizontal, Copy, FileText, CircleX, ArrowDownCircle, CheckCircle2, Eye, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { formatCep, formatCpfCnpj, onlyDigits } from "@/lib/brasil";
import {
  formatBankStatusLabel,
  formatBoletoStatusLabel,
  formatBoletoTypeLabel,
  formatEnvironmentLabel,
  formatOriginLabel,
} from "@/lib/labels";

const STATUSES = ["EMITIDO", "REGISTRADO", "PAGO", "VENCIDO", "CANCELADO", "BAIXADO", "PROTESTADO"];

function formatInputDate(date) {
  return date.toISOString().slice(0, 10);
}

function nextBusinessDate(from = new Date()) {
  const date = new Date(from);
  date.setDate(date.getDate() + 1);
  while ([0, 6].includes(date.getDay())) date.setDate(date.getDate() + 1);
  return formatInputDate(date);
}

function defaultDocNumber() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(now.getDate())}${pad(now.getMonth() + 1)}${String(now.getFullYear()).slice(-2)}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
}

function isSandboxBoleto(boleto) {
  const ambiente = (boleto?.banco_ambiente || "").toLowerCase();
  return ambiente === "sandbox" || (boleto?.bank_code === "237" && ambiente === "homologacao");
}

const BOLETO_ACTIONABLE_STATUSES = new Set(["EMITIDO", "REGISTRADO", "VENCIDO"]);
const BOLETO_CONSULTABLE_STATUSES = new Set(["EMITIDO", "REGISTRADO", "VENCIDO", "PROTESTADO"]);

function boletoStatus(boleto) {
  return String(boleto?.status || "").trim().toUpperCase();
}

function boletoActionLabel(kind) {
  return {
    baixar: "baixa",
    cancelar: "cancelamento",
    "simular-pagamento": "simulação de pagamento",
    consultar: "consulta bancária",
  }[kind] || kind;
}

function boletoStatusReason(status, kind) {
  const actionLabel = boletoActionLabel(kind);
  if (status === "PAGO") return `Boleto já pago não permite ${actionLabel}.`;
  if (status === "BAIXADO") return `Boleto já baixado não permite ${actionLabel}.`;
  if (status === "CANCELADO") return `Boleto já cancelado não permite ${actionLabel}.`;
  if (status === "PROTESTADO") return `Boleto protestado não permite ${actionLabel} por esta tela.`;
  return `Boleto com status ${formatBoletoStatusLabel(status)} não permite ${actionLabel}.`;
}

function getBoletoActionState(boleto, kind) {
  const status = boletoStatus(boleto);
  if (kind === "consultar") {
    const enabled = BOLETO_CONSULTABLE_STATUSES.has(status);
    return { enabled, reason: enabled ? "" : boletoStatusReason(status, kind) };
  }
  if (kind === "simular-pagamento" && !isSandboxBoleto(boleto)) {
    return { enabled: false, reason: "Simulação de pagamento permitida somente em sandbox." };
  }
  const enabled = BOLETO_ACTIONABLE_STATUSES.has(status);
  return { enabled, reason: enabled ? "" : boletoStatusReason(status, kind) };
}

export default function Boletos() {
  const [items, setItems] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [filters, setFilters] = useState({ status: "ALL", bank_code: "ALL", cliente: "" });
  const [openNew, setOpenNew] = useState(false);
  const [detail, setDetail] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (filters.status && filters.status !== "ALL") params.status = filters.status;
    if (filters.bank_code && filters.bank_code !== "ALL") params.bank_code = filters.bank_code;
    if (filters.cliente) params.cliente = filters.cliente;
    const { data } = await api.get("/boletos", { params });
    setItems(data);
  }, [filters]);

  const loadRefs = useCallback(async () => {
    const [{ data: bancosData }, { data: clientesData }] = await Promise.all([
      api.get("/bancos"),
      api.get("/clientes"),
    ]);
    setBancos(bancosData.filter((b) => b.ativo));
    setClientes(clientesData);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRefs(); }, [loadRefs]);
  useEffect(() => {
    setSelectedIds((ids) => ids.filter((id) => items.some((b) => b.id === id)));
  }, [items]);

  const allVisibleSelected = items.length > 0 && items.every((b) => selectedIds.includes(b.id));
  const someVisibleSelected = selectedIds.length > 0 && !allVisibleSelected;

  const toggleAllVisible = (checked) => {
    if (checked) setSelectedIds((ids) => Array.from(new Set([...ids, ...items.map((b) => b.id)])));
    else setSelectedIds((ids) => ids.filter((id) => !items.some((b) => b.id === id)));
  };

  const toggleSelected = (id, checked) => {
    setSelectedIds((ids) => checked ? Array.from(new Set([...ids, id])) : ids.filter((x) => x !== id));
  };

  const refresh = async () => {
    await load();
    if (detail) {
      const { data } = await api.get(`/boletos/${detail.id}`);
      setDetail(data);
    }
  };

  const action = (b, kind) => {
    const state = getBoletoActionState(b, kind);
    if (!state.enabled) {
      toast.error(state.reason);
      return;
    }

    const labels = { baixar: "baixado", cancelar: "cancelado", "simular-pagamento": "pago (sandbox)" };
    const confirmLabels = { baixar: "baixa", cancelar: "cancelamento", "simular-pagamento": "simulação de pagamento" };

    toast(`Confirmar ${confirmLabels[kind] || "ação"} do boleto?`, {
      description: `Documento: ${b.numero_documento}`,
      duration: Infinity,
      action: {
        label: "Confirmar",
        onClick: async () => {
          try {
            let boletoToUpdate = b;
            if ((kind === "baixar" || kind === "cancelar") && !b.bank_registered) {
              const { data: consult } = await api.post(`/boletos/${b.id}/consultar`);
              boletoToUpdate = consult?.boleto || b;
            }
            const refreshedState = getBoletoActionState(boletoToUpdate, kind);
            if (!refreshedState.enabled) throw new Error(refreshedState.reason);

            await api.post(`/boletos/${boletoToUpdate.id}/${kind}`);
            toast.success(`Boleto ${labels[kind]} com sucesso`);
            refresh();
          } catch (err) {
            toast.error(err?.response?.data?.detail || err?.message || "Erro ao processar ação");
          }
        },
      },
      cancel: { label: "Cancelar" },
    });
  };

  const bulkAction = async (kind) => {
    if (!selectedIds.length) return toast.error("Selecione ao menos um boleto");
    setBulkBusy(kind);
    try {
      if (kind === "sync") {
        const { data } = await api.post("/boletos/sincronizar", { ids: selectedIds });
        toast.success(`Consulta concluída: ${data.checked} verificados, ${data.updated} atualizados`);
        await load();
      }
      if (kind === "pdf") {
        const { data } = await api.post("/boletos/exportar-pdfs", { ids: selectedIds }, { responseType: "blob" });
        const url = URL.createObjectURL(new Blob([data], { type: "application/zip" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `boletos_${Date.now()}.zip`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("PDFs exportados");
      }
      if (kind === "email") {
        const { data } = await api.post("/boletos/enviar-email-lote", { ids: selectedIds });
        toast.success(`E-mails enviados: ${data.sent}. Falhas: ${data.failed}. Sem e-mail: ${data.missing_email}.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro na ação em lote");
    } finally {
      setBulkBusy(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="boletos-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Cobrança</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Boletos</h1>
          <p className="text-sm text-zinc-500 mt-1">Emissão híbrida (Boleto + PIX), conciliação e gestão.</p>
        </div>
        <Dialog open={openNew} onOpenChange={(value) => { setOpenNew(value); if (value) loadRefs(); }}>
          <DialogTrigger asChild>
            <Button className="rounded-sm" data-testid="boleto-new-button">
              <Plus className="w-4 h-4 mr-2" /> Emitir boleto
            </Button>
          </DialogTrigger>
          <NewBoletoDialog
            bancos={bancos}
            clientes={clientes}
            onRefreshClientes={loadRefs}
            onCreated={() => { setOpenNew(false); load(); }}
          />
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="rounded-sm border border-zinc-200 shadow-none p-3 flex flex-wrap gap-2 items-center">
        <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
          <SelectTrigger className="w-[160px] h-9 rounded-sm" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todos os status</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{formatBoletoStatusLabel(s)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.bank_code} onValueChange={(v) => setFilters((f) => ({ ...f, bank_code: v }))}>
          <SelectTrigger className="w-[180px] h-9 rounded-sm" data-testid="filter-banco"><SelectValue placeholder="Banco" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todos os bancos</SelectItem>
            {bancos.map((b) => <SelectItem key={b.id} value={b.bank_code}>{b.bank_code} – {b.banco_nome}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input
          placeholder="Buscar por cliente…"
          className="w-[240px] h-9 rounded-sm"
          value={filters.cliente}
          onChange={(e) => setFilters((f) => ({ ...f, cliente: e.target.value }))}
          data-testid="filter-cliente"
        />
        <Button variant="outline" size="sm" className="rounded-sm h-9" onClick={() => setFilters({ status: "ALL", bank_code: "ALL", cliente: "" })} data-testid="filter-clear">
          Limpar
        </Button>
        <div className="ml-auto text-xs text-zinc-500 font-mono tabular">{items.length} boletos</div>
      </Card>

      {selectedIds.length > 0 && (
        <Card className="rounded-sm border border-zinc-300 shadow-none px-3 py-2 flex flex-wrap items-center gap-2 bg-zinc-50">
          <div className="text-xs font-mono text-zinc-600 mr-auto">
            {selectedIds.length} selecionado(s)
          </div>
          <Button variant="outline" size="sm" className="rounded-sm h-9" onClick={() => bulkAction("sync")} disabled={!!bulkBusy} data-testid="bulk-sync">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${bulkBusy === "sync" ? "animate-spin" : ""}`} /> Consultar banco
          </Button>
          <Button variant="outline" size="sm" className="rounded-sm h-9" onClick={() => bulkAction("pdf")} disabled={!!bulkBusy} data-testid="bulk-export-pdf">
            <Download className="w-3.5 h-3.5 mr-1" /> Exportar PDFs
          </Button>
          <Button variant="outline" size="sm" className="rounded-sm h-9" onClick={() => bulkAction("email")} disabled={!!bulkBusy} data-testid="bulk-send-email">
            <Mail className="w-3.5 h-3.5 mr-1" /> Enviar e-mail
          </Button>
          <Button variant="ghost" size="sm" className="rounded-sm h-9" onClick={() => setSelectedIds([])} disabled={!!bulkBusy}>
            Limpar seleção
          </Button>
        </Card>
      )}

      <Card className="rounded-sm border border-zinc-200 shadow-none overflow-x-auto">
        <table className="tbl" data-testid="boletos-table">
          <thead>
            <tr>
              <th className="w-10">
                <Checkbox
                  checked={allVisibleSelected ? true : someVisibleSelected ? "indeterminate" : false}
                  onCheckedChange={(checked) => toggleAllVisible(Boolean(checked))}
                  data-testid="boletos-select-all"
                />
              </th>
              <th>Nº Doc.</th><th>Cliente</th><th>Banco</th><th>Vencimento</th><th className="text-right">Valor</th><th>Status</th><th>Status banco</th><th className="w-12"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={9} className="text-center py-12 text-sm text-zinc-500">Nenhum boleto emitido.</td></tr>}
            {items.map((b) => (
              <tr key={b.id} className="cursor-pointer" onClick={() => setDetail(b)} data-testid={`boleto-row-${b.id}`}>
                <td onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedIds.includes(b.id)}
                    onCheckedChange={(checked) => toggleSelected(b.id, Boolean(checked))}
                    data-testid={`boleto-select-${b.id}`}
                  />
                </td>
                <td className="font-mono text-xs">{b.numero_documento}</td>
                <td>
                  <div className="font-medium text-sm">{b.cliente?.nome}</div>
                  <div className="text-[11px] text-zinc-500 font-mono">{b.cliente?.cpf_cnpj}</div>
                </td>
                <td className="text-xs text-zinc-600">{b.bank_code}</td>
                <td className="font-mono text-xs tabular">{formatDate(b.vencimento)}</td>
                <td className="text-right font-mono tabular font-semibold">{formatBRL(b.valor)}</td>
                <td><span className={`badge-status badge-${b.status}`}>{formatBoletoStatusLabel(b.status)}</span></td>
                <td>
                  <div className="text-xs" title={b.bank_status || ""}>{formatBankStatusLabel(b.bank_status)}</div>
                  <div className="text-[10px] text-zinc-500 font-mono">{b.bank_checked_at ? formatDateTime(b.bank_checked_at) : "não consultado"}</div>
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-1.5 hover:bg-zinc-100 rounded-sm" data-testid={`boleto-actions-${b.id}`}>
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="rounded-sm">
                      <DropdownMenuItem onClick={() => setDetail(b)}><Eye className="w-3.5 h-3.5 mr-2" /> Detalhes</DropdownMenuItem>
                      {isSandboxBoleto(b) && (
                        <DropdownMenuItem
                          disabled={!getBoletoActionState(b, "simular-pagamento").enabled}
                          onClick={() => action(b, "simular-pagamento")}
                          data-testid={`boleto-pay-${b.id}`}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 mr-2" /> Simular pagamento
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        disabled={!getBoletoActionState(b, "baixar").enabled}
                        onClick={() => action(b, "baixar")}
                        data-testid={`boleto-baixar-${b.id}`}
                      >
                        <ArrowDownCircle className="w-3.5 h-3.5 mr-2" /> Baixar
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={!getBoletoActionState(b, "cancelar").enabled}
                        onClick={() => action(b, "cancelar")}
                        data-testid={`boleto-cancelar-${b.id}`}
                        className="text-red-600"
                      >
                        <CircleX className="w-3.5 h-3.5 mr-2" /> Cancelar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <DetailDialog
        boleto={detail}
        onClose={() => setDetail(null)}
        onAction={(kind) => action(detail, kind)}
        onUpdated={(boleto) => { setDetail(boleto); load(); }}
      />
    </div>
  );
}

// ---------- New Boleto Dialog ----------
function NewBoletoDialog({ bancos, clientes, onRefreshClientes, onCreated }) {
  const [form, setForm] = useState(() => ({
    bank_code: "", origem: "avulso", numero_documento: defaultDocNumber(), nosso_numero: "", valor: "", vencimento: nextBusinessDate(),
    tipo: "boleto_hibrido", instrucoes: "", juros_dia: "0", multa: "0", desconto: "0",
    cliente: { nome: "", cpf_cnpj: "", email: "", endereco: "", cidade: "", uf: "", cep: "" },
  }));
  const [pickCliente, setPickCliente] = useState("");
  const [busy, setBusy] = useState(false);
  const selectedCliente = useMemo(() => clientes.find((x) => x.id === pickCliente), [clientes, pickCliente]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const updCliente = (k) => (e) => {
    const raw = e.target.value;
    const value = k === "cpf_cnpj" ? formatCpfCnpj(raw) : k === "cep" ? formatCep(raw) : raw;
    setForm((f) => ({ ...f, cliente: { ...f.cliente, [k]: value } }));
    if (pickCliente) setPickCliente("");
  };

  const selectCliente = (id) => {
    setPickCliente(id);
    const c = clientes.find((x) => x.id === id);
    if (!c) return;
    setForm((f) => ({
      ...f,
      cliente: {
        nome: c.nome, cpf_cnpj: c.cpf_cnpj, email: c.email || "",
        endereco: c.endereco || "", cidade: c.cidade || "", uf: c.estado || "", cep: c.cep || "",
      },
    }));
  };

  const clearCliente = () => {
    setPickCliente("");
    setForm((f) => ({
      ...f,
      cliente: { nome: "", cpf_cnpj: "", email: "", endereco: "", cidade: "", uf: "", cep: "" },
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.bank_code) return toast.error("Selecione um banco");
    if (!form.cliente.nome || !onlyDigits(form.cliente.cpf_cnpj)) return toast.error("Informe o cliente do boleto");
    if (form.bank_code === "001") {
      const due = new Date(`${form.vencimento}T00:00:00`);
      if (!form.vencimento || due <= new Date()) return toast.error("Para Banco do Brasil, informe vencimento futuro em dia útil.");
      if ([0, 6].includes(due.getDay())) return toast.error("Banco do Brasil não aceita vencimento em fim de semana.");
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        nosso_numero: form.nosso_numero?.trim() || null,
        valor: parseFloat(form.valor),
        juros_dia: parseFloat(form.juros_dia || 0),
        multa: parseFloat(form.multa || 0),
        desconto: parseFloat(form.desconto || 0),
      };
      await api.post("/boletos", payload);
      toast.success("Boleto emitido!");
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao emitir");
    } finally { setBusy(false); }
  };

  return (
    <DialogContent className="max-w-4xl rounded-sm max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-display">Emitir novo boleto</DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FieldSelect label="Banco" value={form.bank_code} onValueChange={upd("bank_code")} testid="new-banco">
          {bancos.map((b) => <SelectItem key={b.id} value={b.bank_code}>{b.bank_code} – {b.banco_nome} ({formatEnvironmentLabel(b.ambiente)})</SelectItem>)}
        </FieldSelect>
        <FieldSelect label="Tipo" value={form.tipo} onValueChange={upd("tipo")} testid="new-tipo">
          <SelectItem value="boleto">Boleto</SelectItem>
          <SelectItem value="boleto_hibrido">Boleto híbrido (PIX)</SelectItem>
          <SelectItem value="pix">PIX cobrança</SelectItem>
        </FieldSelect>
        <Field label="Nº Documento" required value={form.numero_documento} onChange={upd("numero_documento")} mono testid="new-numdoc" />
        <Field
          label="Nosso número"
          value={form.nosso_numero}
          onChange={upd("nosso_numero")}
          mono
          testid="new-nosso-numero"
          placeholder="Gerado pelo Hub se vazio"
        />
        <Field label="Valor (BRL)" required type="number" step="0.01" value={form.valor} onChange={upd("valor")} mono testid="new-valor" />
        <Field label="Vencimento" required type="date" min={nextBusinessDate()} value={form.vencimento} onChange={upd("vencimento")} mono testid="new-vencto" />
        <Field label="Juros/dia" type="number" step="0.01" value={form.juros_dia} onChange={upd("juros_dia")} mono testid="new-juros" />
        <Field label="Multa (%)" type="number" step="0.01" value={form.multa} onChange={upd("multa")} mono testid="new-multa" />
        <Field label="Desconto" type="number" step="0.01" value={form.desconto} onChange={upd("desconto")} mono testid="new-desconto" />

        <div className="sm:col-span-2 mt-2 border border-zinc-200 rounded-sm p-3 bg-zinc-50/50">
          <div className="flex flex-wrap items-end justify-between gap-2 mb-3">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500">Cliente</div>
              <div className="text-xs text-zinc-500 mt-1">Pagador da cobrança avulsa.</div>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" className="h-9 rounded-sm" onClick={onRefreshClientes}>
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Atualizar
              </Button>
              <Button type="button" variant="outline" size="sm" className="h-9 rounded-sm" onClick={clearCliente} disabled={!pickCliente && !form.cliente.nome}>
                <Eraser className="w-3.5 h-3.5 mr-1" /> Limpar
              </Button>
            </div>
          </div>

          <CustomerPicker
            clientes={clientes}
            value={pickCliente}
            onChange={selectCliente}
          />

          {selectedCliente && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
              <div className="border border-zinc-200 bg-white rounded-sm p-2">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Selecionado</div>
                <div className="font-semibold mt-0.5">{selectedCliente.nome}</div>
              </div>
              <div className="border border-zinc-200 bg-white rounded-sm p-2">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Documento</div>
                <div className="font-mono mt-0.5">{selectedCliente.cpf_cnpj || "-"}</div>
              </div>
              <div className="border border-zinc-200 bg-white rounded-sm p-2">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Cidade/UF</div>
                <div className="mt-0.5">{selectedCliente.cidade ? `${selectedCliente.cidade}/${selectedCliente.estado || ""}` : "-"}</div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Nome / Razão Social" required value={form.cliente.nome} onChange={updCliente("nome")} testid="new-cli-nome" className="sm:col-span-2" />
            <Field label="CPF / CNPJ" required value={form.cliente.cpf_cnpj} onChange={updCliente("cpf_cnpj")} mono testid="new-cli-cpf" />
            <Field label="E-mail" type="email" value={form.cliente.email} onChange={updCliente("email")} testid="new-cli-email" />
            <Field label="Endereço" value={form.cliente.endereco} onChange={updCliente("endereco")} testid="new-cli-end" className="sm:col-span-2" />
            <Field label="Cidade" value={form.cliente.cidade} onChange={updCliente("cidade")} testid="new-cli-cidade" />
            <Field label="UF" value={form.cliente.uf} onChange={updCliente("uf")} mono testid="new-cli-uf" />
            <Field label="CEP" value={form.cliente.cep} onChange={updCliente("cep")} mono testid="new-cli-cep" />
          </div>
        </div>

        <div className="sm:col-span-2 space-y-1.5">
          <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">Instruções</Label>
          <Textarea rows={2} value={form.instrucoes} onChange={upd("instrucoes")} className="rounded-sm" data-testid="new-instrucoes" />
        </div>

        <DialogFooter className="sm:col-span-2">
          <Button type="submit" disabled={busy} className="rounded-sm" data-testid="new-boleto-submit">
            {busy ? "Emitindo..." : "Emitir boleto"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

// ---------- Detail Dialog ----------
function DetailDialog({ boleto, onClose, onAction, onUpdated }) {
  const [pdfBusy, setPdfBusy] = useState(false);
  const [consulting, setConsulting] = useState(false);
  if (!boleto) return null;
  const copy = (txt, label) => {
    navigator.clipboard.writeText(txt);
    toast.success(`${label} copiado`);
  };
  const openPdf = async () => {
    const tab = window.open("", "_blank");
    setPdfBusy(true);
    try {
      const { data } = await api.get(`/boletos/${boleto.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      if (tab) tab.location.href = url;
      else window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      if (tab) tab.close();
      toast.error(err?.response?.data?.detail || "Não foi possível abrir o PDF");
    } finally {
      setPdfBusy(false);
    }
  };
  const consultarBanco = async () => {
    const state = getBoletoActionState(boleto, "consultar");
    if (!state.enabled) {
      toast.error(state.reason);
      return;
    }

    setConsulting(true);
    try {
      const { data } = await api.post(`/boletos/${boleto.id}/consultar`);
      onUpdated(data.boleto);
      if (data.ok) toast.success("Boleto confirmado no banco");
      else toast.warning(data.boleto?.bank_message || "Boleto não confirmado no banco");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao consultar banco");
    } finally {
      setConsulting(false);
    }
  };
  const bankRegistered = Boolean(boleto.bank_registered);
  const bankStatus = boleto.bank_status || (boleto.provider_mocked ? "MOCK" : "NAO_CONSULTADO");
  const consultarState = getBoletoActionState(boleto, "consultar");
  const simularState = getBoletoActionState(boleto, "simular-pagamento");
  const baixarState = getBoletoActionState(boleto, "baixar");
  const cancelarState = getBoletoActionState(boleto, "cancelar");
  const bankMessage = boleto.bank_message || "Ainda não consultado na plataforma do banco.";
  return (
    <Dialog open={!!boleto} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-3">
            Boleto {boleto.numero_documento}
            <span className={`badge-status badge-${boleto.status}`}>{formatBoletoStatusLabel(boleto.status)}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <Info label="Valor" value={formatBRL(boleto.valor)} mono bold />
          <Info label="Vencimento" value={formatDate(boleto.vencimento)} mono />
          <Info label="Nosso Número" value={boleto.nosso_numero} mono />
          <Info label="Banco" value={boleto.bank_code} mono />
          <Info label="Origem" value={formatOriginLabel(boleto.origem || "erp")} />
          <Info label="Cliente" value={boleto.cliente?.nome} className="col-span-2" />
          <Info label="CPF/CNPJ" value={boleto.cliente?.cpf_cnpj} mono />
          <Info label="Tipo" value={formatBoletoTypeLabel(boleto.tipo)} />
          <Info
            label="Registro no banco"
            value={bankRegistered ? `Confirmado (${formatBankStatusLabel(bankStatus)})` : formatBankStatusLabel(bankStatus)}
          />
          <Info label="Ambiente banco" value={formatEnvironmentLabel(boleto.banco_ambiente)} />
          <Info label="Mensagem banco" value={bankMessage} className="col-span-2" />
        </div>

        <div className="border-t border-zinc-200 pt-4 space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono mb-1">Linha digitável</div>
            <div className="flex items-center gap-2">
              <code className="text-xs font-mono bg-zinc-50 border border-zinc-200 px-3 py-2 rounded-sm flex-1 break-all">{boleto.linha_digitavel}</code>
              <Button size="sm" variant="outline" className="rounded-sm" onClick={() => copy(boleto.linha_digitavel, "Linha digitável")} data-testid="copy-linha">
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          {boleto.pix_copia_cola && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono mb-1">PIX Copia e Cola</div>
              <div className="flex items-center gap-2">
                <code className="text-xs font-mono bg-zinc-50 border border-zinc-200 px-3 py-2 rounded-sm flex-1 break-all line-clamp-2">{boleto.pix_copia_cola}</code>
                <Button size="sm" variant="outline" className="rounded-sm" onClick={() => copy(boleto.pix_copia_cola, "PIX")} data-testid="copy-pix">
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}

          {boleto.pix_qrcode && (
            <div className="flex items-center gap-4 pt-2">
              <img src={boleto.pix_qrcode} alt="QR Code PIX" className="w-32 h-32 border border-zinc-200 rounded-sm" data-testid="pix-qrcode" />
              <div className="text-xs text-zinc-500">
                <div className="font-mono uppercase tracking-[0.18em] text-[10px] mb-1">QR Code PIX</div>
                <div>Escaneie no app do banco para pagar.</div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-zinc-200 pt-4 gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm mr-auto"
            onClick={openPdf}
            disabled={pdfBusy}
            data-testid="boleto-pdf"
          >
            <FileText className="w-3.5 h-3.5 mr-1" /> {pdfBusy ? "Abrindo..." : "Abrir PDF"}
          </Button>
          <Button
            variant={bankRegistered ? "secondary" : "outline"}
            size="sm"
            className="rounded-sm"
            onClick={consultarBanco}
            disabled={consulting || !consultarState.enabled}
            title={consultarState.enabled ? "" : consultarState.reason}
            data-testid="detail-consultar-banco"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${consulting ? "animate-spin" : ""}`} />
            {consulting ? "Consultando..." : "Consultar banco"}
          </Button>
          {isSandboxBoleto(boleto) && (
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm"
              onClick={() => onAction("simular-pagamento")}
              disabled={!simularState.enabled}
              title={simularState.enabled ? "" : simularState.reason}
              data-testid="detail-simular-pagamento"
            >
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Simular pagamento
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm"
            onClick={() => onAction("baixar")}
            disabled={!baixarState.enabled}
            title={baixarState.enabled ? "" : baixarState.reason}
            data-testid="detail-baixar"
          >
            <ArrowDownCircle className="w-3.5 h-3.5 mr-1" /> Baixar
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm text-red-600"
            onClick={() => onAction("cancelar")}
            disabled={!cancelarState.enabled}
            title={cancelarState.enabled ? "" : cancelarState.reason}
            data-testid="detail-cancelar"
          >
            <CircleX className="w-3.5 h-3.5 mr-1" /> Cancelar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value, mono, bold, className = "" }) {
  return (
    <div className={className}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">{label}</div>
      <div className={`${mono ? "font-mono tabular" : ""} ${bold ? "font-semibold" : ""} text-sm mt-0.5`}>{value || "-"}</div>
    </div>
  );
}

function Field({ label, mono, className = "", testid, ...props }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">{label}</Label>
      <Input {...props} className={`rounded-sm h-10 ${mono ? "font-mono text-xs" : ""}`} data-testid={testid} />
    </div>
  );
}

function FieldSelect({ label, value, onValueChange, children, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">{label}</Label>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger className="rounded-sm h-10" data-testid={testid}><SelectValue placeholder="Selecione…" /></SelectTrigger>
        <SelectContent>{children}</SelectContent>
      </Select>
    </div>
  );
}

function CustomerPicker({ clientes, value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = clientes.find((c) => c.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between rounded-sm h-auto min-h-11 px-3 py-2 bg-white"
          data-testid="new-pick-cliente"
        >
          <span className="min-w-0 text-left">
            <span className="block truncate text-sm font-medium">
              {selected ? selected.nome : "Buscar cliente cadastrado..."}
            </span>
            <span className="block truncate text-[11px] text-zinc-500 font-mono">
              {selected ? `${selected.cpf_cnpj || "-"} ${selected.email ? `- ${selected.email}` : ""}` : `${clientes.length} clientes disponíveis`}
            </span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(720px,calc(100vw-2rem))] p-0 rounded-sm" align="start">
        <Command filter={(valueText, search) => {
          const haystack = valueText.toLowerCase();
          return haystack.includes(search.toLowerCase()) ? 1 : 0;
        }}>
          <CommandInput placeholder="Digite nome, CPF/CNPJ ou e-mail..." />
          <CommandList>
            <CommandEmpty>Nenhum cliente encontrado.</CommandEmpty>
            <CommandGroup>
              {clientes.map((c) => {
                const searchValue = `${c.nome || ""} ${c.cpf_cnpj || ""} ${c.email || ""} ${c.cidade || ""}`;
                return (
                  <CommandItem
                    key={c.id}
                    value={searchValue}
                    onSelect={() => { onChange(c.id); setOpen(false); }}
                    className="items-start py-2"
                  >
                    <Check className={cn("mt-0.5 h-4 w-4", value === c.id ? "opacity-100" : "opacity-0")} />
                    <div className="min-w-0">
                      <div className="font-medium truncate">{c.nome}</div>
                      <div className="text-[11px] text-zinc-500 font-mono truncate">{c.cpf_cnpj || "-"} {c.email ? `- ${c.email}` : ""}</div>
                      {(c.cidade || c.estado) && <div className="text-[11px] text-zinc-500 truncate">{c.cidade || ""}{c.estado ? `/${c.estado}` : ""}</div>}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
